import { NgClass } from '@angular/common';
import {
  ChangeDetectorRef,
  Component,
  computed,
  ElementRef,
  inject,
  input,
  output,
  signal,
  untracked,
  viewChild,
  viewChildren,
} from '@angular/core';
import { Store } from '@ngrx/store';
import { TranslateModule } from '@ngx-translate/core';
import * as fromSeriousSystem from '@serious-system';
import { AnalyticsService } from '../../../analytics';
import * as fromLayout from '../../../layout';
import { NEW_CHAT_TITLE_DEFAULT } from '../../chats.constants';
import { ChatsHelper } from '../../chats.helper';
import { ChatNavigationItem } from '../../chats.interfaces';
import { RouterService } from '../../shared/services/router.service';
import { ChatsActions, chatsFeature, ChatsState } from '../../store';
import {
  CHAT_TITLE_MAX_LENGTH,
  CHAT_TITLE_PATTERN,
  getChatRenameCommit,
} from './navigation-chats-list.helper';

@Component({
  selector: 'squadbox-navigation-chats-list-smart',
  imports: [
    TranslateModule,
    fromSeriousSystem.UseUIBasicIconDirective,
    fromSeriousSystem.DropdownMenuComponent,
    fromSeriousSystem.DropdownMenuTriggerDirective,
    fromSeriousSystem.UseNavigationIconDirective,
    NgClass,
  ],
  template: `
    <div class="flex flex-1 flex-col gap-1">
      <div class="navigation-label">
        {{ label() }}
      </div>
      @if (!chatNavigationItems().length) {
        <div class="navigation-empty-state">
          <svg
            sdUseNavigationIcon="new-chat"
            class="size-5 shrink-0 text-primary-300"
          ></svg>
          <span class="typo-caption">{{
            'MAIN_NAVIGATION_ITEMS.NO_CHATS' | translate
          }}</span>
        </div>
      } @else {
        @for (chat of chatNavigationItems(); track chat.uuid) {
          @if (editingChatId() === chat.uuid) {
            <div
              class="
                navigation-item
                h-10! rounded! bg-primary-50! p-2!
              "
              [style.view-transition-name]="
                chat.uuid === renameInvokerId() ? 'chat-rename-row' : null
              "
            >
              <input
                #renameInput
                type="text"
                [value]="editingInitialTitle()"
                [attr.maxlength]="CHAT_TITLE_MAX_LENGTH"
                [attr.pattern]="CHAT_TITLE_PATTERN.source"
                [attr.aria-label]="
                  'CHAT.RENAME_CHAT_MODAL.PLACEHOLDER' | translate
                "
                class="
                  typo-p3
                  h-8 w-full min-w-0
                  rounded-lg border border-neutral-200
                  bg-white px-4 py-2
                  cursor-text select-text
                  text-neutral-700 caret-primary-500
                  focus:border-primary-500 focus:outline-hidden
                "
                (keydown.enter)="commitRename($event, chat, renameInput.value)"
                (keydown.escape)="cancelRenameAndRestoreFocus($event)"
                (blur)="handleRenameBlur(chat, renameInput.value)"
              />
            </div>
          } @else {
            <div
              #chatRow
              role="button"
              tabindex="0"
              [attr.data-chat-uuid]="chat.uuid"
              class="navigation-item group tablet-portrait:h-10!"
              [style.view-transition-name]="
                chat.uuid === renameInvokerId() ? 'chat-rename-row' : null
              "
              [ngClass]="{
                selected: selectedChatUuid() === chat.uuid,
              }"
              (click)="handleClickChatCTA(chat)"
              (keydown.enter)="handleClickChatCTA(chat)"
              (keydown.space)="
                handleClickChatCTA(chat); $event.preventDefault()
              "
            >
              <span class="truncate">{{
                chat.title === NEW_CHAT_TITLE_DEFAULT
                  ? (chat.title | translate)
                  : chat.title
              }}</span>
              @if (!isMobile() && !chat.isDraft) {
                <!-- A draft has no menu until POST /chats lands: Rename and
                      Delete call endpoints the chat does not exist on yet.
                     a11y note: the dropdown trigger lives inside an interactive
                      row (outer div is role="button"). Nesting role="button" is
                      invalid ARIA; splitting the row into sibling focusable
                      controls is deferred work (SB-3479). Until then the
                      trigger stays keyboard-operable itself: focusable,
                      visible on focus, Enter/Space/ArrowDown open the menu.
                      The outer row's keydown handlers still open the chat. -->
                <svg
                  sdUseUIBasicIcon="more-horizontal"
                  role="button"
                  tabindex="0"
                  [attr.aria-label]="
                    'CHAT.DROPDOWN_MENU.TRIGGER_LABEL' | translate
                  "
                  class="
                    size-6 shrink-0
                    aria-expanded:text-primary-500 aria-expanded:opacity-100
                    text-neutral-700
                    group-hover:ml-2 aria-expanded:ml-2 focus-visible:ml-2
                    focus:outline-hidden
                    focus-visible:outline-2 focus-visible:outline-primary-500
                    opacity-0 group-hover:opacity-100 focus-visible:opacity-100
                  "
                  [sdDropdownMenuTrigger]="chatDropdown"
                  (click)="handleClickChatDropdownCTA($event, chat)"
                  (keydown.enter)="armChatDropdown($event, chat)"
                  (keydown.space)="armChatDropdown($event, chat)"
                  (keydown.arrowDown)="armChatDropdown($event, chat)"
                ></svg>
              }
            </div>
          }
        }
      }
      <!-- Chat Dropdown Menu -->
      <sd-dropdown-menu
        #chatDropdown
        [options]="chatDropdownMenuOptions()"
        [responsive]="false"
      ></sd-dropdown-menu>
    </div>
  `,
})
export class NavigationChatsListSmartComponent {
  private readonly changeDetectorRef = inject(ChangeDetectorRef);
  private readonly layoutStore = inject<Store<fromLayout.LayoutState>>(Store);
  private readonly chatsStore = inject<Store<ChatsState>>(Store);
  protected readonly chatsHelper = inject(ChatsHelper);
  private readonly routerService = inject(RouterService);
  private readonly analyticsService = inject(AnalyticsService);
  private readonly renameInput =
    viewChild<ElementRef<HTMLInputElement>>('renameInput');
  private readonly chatRows = viewChildren<ElementRef<HTMLElement>>('chatRow');
  protected readonly renameInvokerId = signal<string | null>(null);

  public readonly label = input.required<string>();
  public readonly chatNavigationItems = input.required<ChatNavigationItem[]>();
  public readonly source = input.required<'menu' | 'skill'>();
  public readonly chatDeleteClicked = output<string>();
  public readonly chatClicked = output<void>();

  public readonly isMobile = this.layoutStore.selectSignal(
    fromLayout.layoutFeature.selectIsMobile,
  );
  public readonly selectedChatUuid = this.chatsStore.selectSignal(
    chatsFeature.selectSelectedChatUuid,
  );
  public readonly chatDropdownActiveChat = signal<ChatNavigationItem | null>(
    null,
  );
  public readonly editingChatId = signal<string | null>(null);
  /**
   * Seeded once on entering edit mode: a live store binding would wipe the
   * user's in-progress typing whenever the title changes underneath (e.g. a
   * socket auto-title landing mid-edit).
   */
  protected readonly editingInitialTitle = signal('');
  public readonly NEW_CHAT_TITLE_DEFAULT = NEW_CHAT_TITLE_DEFAULT;
  protected readonly CHAT_TITLE_MAX_LENGTH = CHAT_TITLE_MAX_LENGTH;
  protected readonly CHAT_TITLE_PATTERN = CHAT_TITLE_PATTERN;

  public readonly chatDropdownMenuOptions = computed<
    fromSeriousSystem.DropdownMenuOption[]
  >(() => {
    return this.chatsHelper.getChatDropdownMenuOptions({
      delete: () => {
        untracked(() => {
          const chatUuidToDelete = this.chatDropdownActiveChat()?.uuid;
          if (chatUuidToDelete) {
            this.chatDeleteClicked.emit(chatUuidToDelete);
          }
        });
      },
      rename: () => {
        untracked(() => {
          const chat = this.chatDropdownActiveChat();
          if (chat) {
            this.enterEditMode(chat);
          }
        });
      },
    });
  });

  public async handleClickChatCTA(chat: ChatNavigationItem) {
    if (this.editingChatId() === chat.uuid) {
      return;
    }

    // The address is the chat's uuid alone (SB-3944 / KD-25): the skill is not
    // carried in the URL of a chat that exists. A skill draft keeps its hint
    // until POST /chats lands, so a reload before then still opens the skill.
    const navigated =
      chat.isDraft && chat.assistantId
        ? await this.routerService.navigateToChat(chat.uuid, {
            assistantUuid: chat.assistantId,
          })
        : await this.routerService.navigateToChat(chat.uuid);

    // Fire recent_chat_opened event only if navigation succeeded (guard-cancellations return false).
    // A draft has no server id yet (`Chat.id` is 0): it is not a recent chat.
    if (navigated && !chat.isDraft) {
      this.analyticsService
        .sendRecentChatOpenedEvent({
          source: this.source(),
          conversationId: chat.id,
          ...(chat.assistantId && { assistantId: chat.assistantId }),
          ...(chat.startedAsDefaultChat && { startedAsDefaultChat: true }),
          ...(chat.teammateId && { teammateId: chat.teammateId }),
        })
        .subscribe();
    }

    this.chatClicked.emit();
  }

  public handleClickChatDropdownCTA(
    event: MouseEvent,
    chat: ChatNavigationItem,
  ) {
    event.stopPropagation();
    this.chatDropdownActiveChat.set(chat);
  }

  public armChatDropdown(event: Event, chat: ChatNavigationItem) {
    // CdkMenuTrigger on the same element opens the menu for Enter/Space/
    // ArrowDown (it also preventDefaults Space); this handler only arms the
    // target chat and keeps the key from bubbling into the row's
    // open-the-chat handlers.
    event.stopPropagation();
    this.chatDropdownActiveChat.set(chat);
  }

  public enterEditMode(chat: ChatNavigationItem) {
    this.renameInvokerId.set(chat.uuid);
    this.editingInitialTitle.set(this.chatsHelper.getChatTitle(chat.title));
    this.changeDetectorRef.detectChanges();
    this.updateEditingChatId(chat.uuid, () => {
      const input = this.renameInput()?.nativeElement;
      input?.focus();
      // The prefilled title opens fully selected (type-to-replace).
      input?.select();
    });
  }

  public commitRename(
    event: Event,
    chat: ChatNavigationItem,
    rawValue: string,
  ) {
    event.preventDefault();
    event.stopPropagation();
    this.commitRenameValue(chat, rawValue);
  }

  public cancelRenameAndRestoreFocus(event?: Event) {
    event?.preventDefault();
    event?.stopPropagation();
    this.exitEditModeAndRestoreFocus();
  }

  public handleRenameBlur(chat: ChatNavigationItem, rawValue: string) {
    window.setTimeout(() => {
      if (this.editingChatId() === chat.uuid) {
        this.commitRenameValue(chat, rawValue);
      }
    });
  }

  private commitRenameValue(chat: ChatNavigationItem, rawValue: string) {
    const rename = getChatRenameCommit(
      chat,
      this.chatsHelper.getChatTitle(chat.title),
      rawValue,
    );

    if (rename) {
      this.chatsStore.dispatch(
        ChatsActions.renameChat({
          uuid: chat.uuid,
          title: rename.title,
          previousTitle: rename.previousTitle,
          requestId: crypto.randomUUID(),
          renamedAt: new Date().toISOString(),
          source: 'desktop-inline',
        }),
      );
    }

    this.exitEditModeAndRestoreFocus();
  }

  private exitEditModeAndRestoreFocus() {
    if (this.editingChatId() === null) {
      return;
    }

    this.updateEditingChatId(null, () => {
      const renameInvokerId = this.renameInvokerId();
      const row = this.chatRows().find(
        (chatRow) =>
          chatRow.nativeElement.dataset['chatUuid'] === `${renameInvokerId}`,
      );

      row?.nativeElement.focus();
    });
  }

  private updateEditingChatId(
    chatUuid: string | null,
    onUpdateComplete?: () => void,
  ) {
    const update = () => {
      this.editingChatId.set(chatUuid);
      this.changeDetectorRef.detectChanges();
    };
    const viewTransitionDocument =
      typeof document === 'undefined' ? undefined : document;
    const prefersReducedMotion =
      typeof window !== 'undefined' &&
      window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;

    if (viewTransitionDocument?.startViewTransition && !prefersReducedMotion) {
      const viewTransition = viewTransitionDocument.startViewTransition(update);
      if (onUpdateComplete) {
        void viewTransition.updateCallbackDone.then(onUpdateComplete);
      }
      return;
    }

    update();
    onUpdateComplete?.();
  }
}
